import { Navigate, Route, Routes } from 'react-router';

import { useScrollToTop } from '@mds/shared/hooks/use-scroll-to-top';

import RequireAuth from './auth/require-auth';
import RequirePermission from './auth/require-permission';
import AdminLayout from './layouts/admin-layout';
import AdministratorsPage from './pages/administrators-page';
import AnalyticsPage from './pages/analytics-page';
import ApplicationsPage from './pages/applications-page';
import AuditLogPage from './pages/audit-log-page';
import BookingsPage from './pages/bookings-page';
import BookingFeedbackPage from './pages/booking-feedback-page';
import BookingRequestsPage from './pages/booking-requests-page';
import BookingPolicyPage from './pages/booking-policy-page';
import CafePage from './pages/cafe-page';
import AttendeesPage from './pages/attendees-page';
import CheckInPage from './pages/check-in-page';
import CommunicationsPage from './pages/communications-page';
import CustomersPage from './pages/customers-page';
import DashboardPage from './pages/dashboard-page';
import EventPage from './pages/event-page';
import EventsPage from './pages/events-page';
import ExpensesPage from './pages/expenses-page';
import FilesPage from './pages/files-page';
import ForgotPasswordPage from './pages/forgot-password-page';
import LoginPage from './pages/login-page';
import NotFoundPage from './pages/not-found-page';
import NotificationsPage from './pages/notifications-page';
import PackagesPage from './pages/packages-page';
import PaymentsPage from './pages/payments-page';
import ProposalsPage from './pages/proposals-page';
import ResetPasswordPage from './pages/reset-password-page';
import RolesPage from './pages/roles-page';
import SpacesPage from './pages/spaces-page';
import SurveyPage from './pages/survey-page';

/** The admin route table. Every page renders inside AdminLayout. */
export default function App() {
  useScrollToTop();
  return (
    <Routes>
      {/* Signed out. These three are the only routes reachable without a session. */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route element={<RequireAuth />}>
        <Route element={<AdminLayout />}>
          <Route index element={<DashboardPage />} />

          {/* The page was called Desks before it covered rooms too. */}
          <Route path="desks" element={<Navigate to="/spaces" replace />} />

          {/* Guarded twice: the nav hides these, and a typed URL still has to
              clear the permission. */}
          <Route element={<RequirePermission permission="reservations.read" />}>
            <Route path="bookings" element={<BookingsPage />} />
            <Route path="bookings/feedback" element={<BookingFeedbackPage />} />
            <Route path="booking-requests" element={<BookingRequestsPage />} />
            <Route path="proposals" element={<ProposalsPage />} />
          </Route>
          <Route element={<RequirePermission permission="applications.read" />}>
            <Route path="careers" element={<ApplicationsPage />} />
          </Route>
          <Route element={<RequirePermission permission="events.read" />}>
            <Route path="events" element={<EventsPage />} />
            <Route path="events/attendees" element={<AttendeesPage />} />
            {/* The old per-event attendee page; its links now land on Attendees. */}
            <Route path="events/:eventId/:tab?" element={<EventPage />} />
          </Route>
          <Route element={<RequirePermission permission="events.checkin" />}>
            <Route path="check-in" element={<CheckInPage />} />
          </Route>
          {/* Spaces is the inventory as well as the board, so it turns on the
              inventory permission and asks for reservations.read in the page. */}
          <Route element={<RequirePermission permission="facilities.read" />}>
            <Route path="spaces" element={<SpacesPage />} />
          </Route>
          <Route element={<RequirePermission permission="payments.read" />}>
            <Route path="payments" element={<PaymentsPage />} />
          </Route>
          <Route element={<RequirePermission permission="expenses.read" />}>
            <Route path="expenses" element={<ExpensesPage />} />
          </Route>
          <Route element={<RequirePermission permission="crm.customers.read" />}>
            <Route path="customers" element={<CustomersPage />} />
          </Route>
          <Route element={<RequirePermission permission="surveys.read" />}>
            <Route path="surveys/:surveyKey" element={<SurveyPage />} />
          </Route>
          <Route element={<RequirePermission permission="facilities.read" />}>
            <Route path="packages" element={<PackagesPage />} />
          </Route>
          <Route element={<RequirePermission permission="admin.users.read" />}>
            <Route path="administrators" element={<AdministratorsPage />} />
            <Route path="roles" element={<RolesPage />} />
          </Route>
          <Route element={<RequirePermission permission="reports.read" />}>
            <Route path="analytics" element={<AnalyticsPage />} />
          </Route>
          <Route element={<RequirePermission permission="audit.read" />}>
            <Route path="audit-log" element={<AuditLogPage />} />
          </Route>
          <Route element={<RequirePermission permission="content.read" />}>
            <Route path="files" element={<FilesPage />} />
            <Route path="cafe" element={<CafePage />} />
            <Route path="booking-policy" element={<BookingPolicyPage />} />
          </Route>
          <Route element={<RequirePermission permission="communications.read" />}>
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="communications" element={<CommunicationsPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
